import * as React from 'react';
import { Location, Params, To, useLocation, useNavigate, useParams } from 'react-router';

// The react-router 5 route props (match, location, history) that the class components were written against.
export interface RouteComponentProps {
  history: {
    push(to: To): void;
    replace(to: To): void;
    go(delta: number): void;
    goBack(): void;
    goForward(): void;
  };
  location: Location;
  match: {
    params: Readonly<Params>;
    url: string;
    isExact: boolean;
  };
}

export function withRouter<P extends RouteComponentProps>(Component: React.ComponentType<P>) {
  function WithRouter(props: Omit<P, keyof RouteComponentProps>) {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    const history = React.useMemo<RouteComponentProps['history']>(
      () => ({
        push: to => navigate(to),
        replace: to => navigate(to, { replace: true }),
        go: delta => navigate(delta),
        goBack: () => navigate(-1),
        goForward: () => navigate(1),
      }),
      [navigate],
    );
    // Every route is matched exactly, so the matched url is the whole pathname.
    const match = { params, url: location.pathname, isExact: true };

    return <Component {...({ ...props, history, location, match } as P)} />;
  }
  WithRouter.displayName = `withRouter(${Component.displayName || Component.name})`;

  return WithRouter;
}
